// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  PolicyDocumentUploadResult,
  PolicyDocumentVersionInfo,
  PolicyDocumentExistsResponse,
  UploadProgress
} from '../types/policyDocument';

export const policyDocumentApi = {
  /**
   * Upload a document for a policy
   */
  uploadDocument: async (
    policyId: string,
    file: File,
    changeSummary?: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<PolicyDocumentUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    if (changeSummary) {
      formData.append('changeSummary', changeSummary);
    }

    const response = await api.post<PolicyDocumentUploadResult>(
      `/policies/${policyId}/documents`,
      formData,
      {
        // multipart/form-data: axios 1.x detects the FormData body and
        // rewrites this with the correct `; boundary=...`. (`false` does NOT
        // strip the api instance's default application/json in axios 1.6 —
        // it's ignored — so the server got multipart with no boundary and
        // failed with "Required part 'file' is not present".)
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
            });
          }
        }
      }
    );
    return response.data;
  },

  /**
   * Upload a document on behalf of an advisor (for support staff)
   */
  uploadDocumentOnBehalf: async (
    policyId: string,
    advisorId: string,
    file: File,
    changeSummary?: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<PolicyDocumentUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    if (changeSummary) {
      formData.append('changeSummary', changeSummary);
    }

    const response = await api.post<PolicyDocumentUploadResult>(
      `/policies/${policyId}/documents/on-behalf-of/${advisorId}`,
      formData,
      {
        // multipart/form-data: axios 1.x detects the FormData body and
        // rewrites this with the correct `; boundary=...`. (`false` does NOT
        // strip the api instance's default application/json in axios 1.6 —
        // it's ignored — so the server got multipart with no boundary and
        // failed with "Required part 'file' is not present".)
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress({
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total)
            });
          }
        }
      }
    );
    return response.data;
  },

  /**
   * Download the latest version of a policy document
   */
  downloadLatest: async (policyId: string): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/policies/${policyId}/documents`, {
      responseType: 'blob'
    });

    // Extract filename from Content-Disposition header
    const contentDispositionRaw = response.headers['content-disposition'];
    const contentDisposition = typeof contentDispositionRaw === 'string' ? contentDispositionRaw : undefined;
    let filename = 'document';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1].replaceAll('"', '');
      }
    }

    return { blob: response.data, filename };
  },

  /**
   * Download a specific version of a policy document
   */
  downloadVersion: async (policyId: string, version: number): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/policies/${policyId}/documents/versions/${version}`, {
      responseType: 'blob'
    });

    // Extract filename from Content-Disposition header
    const contentDispositionRaw = response.headers['content-disposition'];
    const contentDisposition = typeof contentDispositionRaw === 'string' ? contentDispositionRaw : undefined;
    let filename = 'document';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1].replaceAll('"', '');
      }
    }

    return { blob: response.data, filename };
  },

  /**
   * List all versions of a policy document
   */
  listVersions: async (policyId: string): Promise<PolicyDocumentVersionInfo[]> => {
    const response = await api.get<PolicyDocumentVersionInfo[]>(
      `/policies/${policyId}/documents/versions`
    );
    return response.data;
  },

  /**
   * Check if a policy has documents
   */
  checkExists: async (policyId: string): Promise<PolicyDocumentExistsResponse> => {
    const response = await api.get<PolicyDocumentExistsResponse>(
      `/policies/${policyId}/documents/exists`
    );
    return response.data;
  },

  /**
   * Delete a specific version of a policy document (soft delete)
   */
  deleteVersion: async (policyId: string, version: number): Promise<void> => {
    await api.delete(`/policies/${policyId}/documents/versions/${version}`);
  }
};
